import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ICS_FLAGS } from "@nav-games/flags";
import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
  pickWeightedKey,
  statsFor,
} from "@nav-games/games-core";

import { Flag } from "../../ui/Flag";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface ReverseProps {
  onNavigate: (to: string) => void;
}

const MODULE = "flags-reverse";
const GAME = "reverse";
const CARDS_PER_SESSION = 15;
const BEAT_THE_CLOCK_MS = 90_000;
const CHOICES_PER_CARD = 4;

/// Pool of single-flag letters with an IRPCS / ICS solo meaning.
/// Same eligibility as the existing `Meanings` game — R is excluded
/// (no solo meaning), and digit pennants are excluded (no
/// operational meaning beyond the digit).
const POOL: readonly string[] = Object.entries(ICS_FLAGS)
  .filter(
    ([, spec]) =>
      spec.shortMeaning !== null &&
      spec.kind !== "special" &&
      spec.kind !== "nato-digit",
  )
  .map(([id]) => id);

// Recent answers kept out of the next draw so the spaced-repetition
// picker doesn't resurface the same card within a short window.
// Clamped to pool size - 1 so there is always an eligible card.
const AVOID_WINDOW = Math.min(8, POOL.length - 1);

type SessionMode = "cards" | "timed";

interface Card {
  answer: string;
  choices: readonly string[];
}

function nextCard(recent: readonly string[]): Card {
  const answer = pickWeightedKey(MODULE, POOL, recent);
  const distractors = new Set<string>();
  while (distractors.size < CHOICES_PER_CARD - 1) {
    const d = POOL[Math.floor(Math.random() * POOL.length)]!;
    if (d !== answer) distractors.add(d);
  }
  const choices = shuffle([answer, ...distractors]);
  return { answer, choices };
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

export function Reverse({ onNavigate }: ReverseProps) {
  const [sessionMode, setSessionMode] = useState<SessionMode | null>(null);
  if (!sessionMode) {
    return (
      <Picker
        onStart={setSessionMode}
        onBack={() => onNavigate("/play/flags")}
      />
    );
  }
  return (
    <Game
      sessionMode={sessionMode}
      onAgain={() => setSessionMode(sessionMode)}
      onBack={() => onNavigate("/play/flags")}
    />
  );
}

function Picker({
  onStart,
  onBack,
}: {
  onStart: (m: SessionMode) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>Reverse: meaning → flag</h2>
      <p className="muted">
        Each card shows the meaning of an ICS single-flag signal. Tap the
        flag that carries it.
      </p>
      <div className="card">
        <h3>Session length</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart("cards")}>{CARDS_PER_SESSION} cards</button>
          <button className="secondary" onClick={() => onStart("timed")}>
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} seconds
          </button>
        </div>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back to Flags
      </button>
    </>
  );
}

interface GameProps {
  sessionMode: SessionMode;
  onAgain: () => void;
  onBack: () => void;
}

function Game({ sessionMode, onAgain, onBack }: GameProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    [sessionMode],
  );

  const recentRef = useRef<string[]>([]);
  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard([]);
    recentRef.current = [c.answer];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(recentRef.current);
    recentRef.current = [...recentRef.current, next.answer].slice(-AVOID_WINDOW);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [index, recorder, sessionMode]);

  const onChoose = useCallback(
    (choice: string) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: card.answer,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      setTimeout(advance, ok ? 600 : 1500);
    },
    [advance, card, feedback, recorder, result, shownAt],
  );

  if (result) {
    return <EndCard result={result} onAgain={onAgain} onBack={onBack} />;
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Card ${index + 1} of ${CARDS_PER_SESSION}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right`;

  const answerSpec = ICS_FLAGS[card.answer]!;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "flags" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">{header}</p>
      <div className="card" style={{ textAlign: "center" }}>
        <p
          style={{
            fontSize: 22,
            margin: "16px 0 24px",
            minHeight: "2.5em",
          }}
        >
          {answerSpec.shortMeaning ?? "—"}
        </p>
        {feedback && (
          <p className="small muted" style={{ marginTop: 0, marginBottom: 8 }}>
            {feedback === "right" ? "✓ " : "✗ "}
            {answerSpec.name} — {answerSpec.meaning}
          </p>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 8,
            marginTop: 8,
          }}
        >
          {card.choices.map((c) => (
            <button
              key={c}
              className="secondary"
              disabled={!!feedback}
              onClick={() => onChoose(c)}
              style={{
                padding: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                background:
                  feedback && c === card.answer
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              <Flag char={c} size={64} />
              <span className="small muted">{c}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function EndCard({
  result,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  onAgain: () => void;
  onBack: () => void;
}) {
  const rolling = POOL
    .map((c) => ({ char: c, ...statsFor(MODULE, c) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);

  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}.
        </p>
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest meanings (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.char}>
                <code>{w.char}</code> — {ICS_FLAGS[w.char]?.shortMeaning ?? ""}: {" "}
                {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Flags
        </button>
      </div>
    </>
  );
}
